import { useLocation, Link } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";

/**
 * Maps route path segments to display labels for breadcrumbs.
 * Extends the sidebar navigation groups for consistency.
 */
const ROUTE_LABELS: Record<string, string> = {
  "": "Dashboard",
  campaigns: "Campaigns",
  campaign: "Campaign",
  mail: "Mail Library",
  "landing-pages": "Landing Pages",
  forms: "Forms",
  docs: "Forms Guide",
  contacts: "Contacts",
  lists: "Lists & Segments",
  import: "Import Contacts",
  marketing: "Marketing",
  broadcasts: "Broadcasts",
  automations: "Automations",
  content: "Content Library",
  "gift-cards": "Gift Cards",
  "call-center": "Call Center",
  scripts: "Call Scripts",
  monitoring: "Activity & Logs",
  client: "My Account",
  billing: "Billing",
  "agency-management": "Client Management",
  agency: "Agency",
  admin: "Admin",
  organizations: "Organizations",
  users: "User Management",
  "gift-cards-dashboard": "Gift Cards",
  "messaging-test": "Messaging Test",
  integrations: "Integrations",
  settings: "Settings",
  account: "Account",
  company: "Company",
  communications: "Communications",
  team: "Team",
  domains: "Domains",
  general: "General",
  "api-docs": "API Docs",
};

function getLabel(segment: string, prevSegment?: string): string {
  if (prevSegment === "forms" && segment === "docs") return "Forms Guide";
  if (segment === "docs") return "Help";
  return ROUTE_LABELS[segment] ?? segment.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function Breadcrumbs() {
  const location = useLocation();
  const pathname = location.pathname;

  // Skip breadcrumbs on auth, public, or root pages
  if (pathname === "/" || pathname.startsWith("/auth") || pathname === "") {
    return (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbPage>Dashboard</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    );
  }

  const segments = pathname.split("/").filter(Boolean);

  if (segments.length === 0) {
    return null;
  }

  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to="/">Dashboard</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        {segments.map((segment, i) => {
          const href = "/" + segments.slice(0, i + 1).join("/");
          const prevSegment = i > 0 ? segments[i - 1] : undefined;
          const label = getLabel(segment, prevSegment);
          const isLast = i === segments.length - 1;
          const isId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment);

          if (isId) {
            return (
              <BreadcrumbItem key={href}>
                <BreadcrumbSeparator />
                <BreadcrumbPage className="max-w-[120px] truncate">{segment.slice(0, 8)}...</BreadcrumbPage>
              </BreadcrumbItem>
            );
          }

          return (
            <BreadcrumbItem key={href}>
              <BreadcrumbSeparator />
              {isLast ? (
                <BreadcrumbPage>{label}</BreadcrumbPage>
              ) : (
                <BreadcrumbLink asChild>
                  <Link to={href}>{label}</Link>
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
